import { useCallback, useEffect, useRef, useState } from 'react'
import { getNotebook } from '../services/notebooks'

// Caches full notebook pages (with blocks and populated items) by id. Grid
// windows persist only entityId, so on reload we fetch any pages we don't
// already have. Callers can `seed` a page they already hold and `remove` after
// delete. Fetches are de-duped per id.
export function useNotebookCache(entityIds) {
  const [pagesById, setPagesById] = useState({})
  const pendingRef = useRef(new Set())

  useEffect(() => {
    entityIds.forEach((id) => {
      if (!id || pendingRef.current.has(id)) return
      // List stubs have no `blocks`; only skip the fetch once we hold a full page.
      const existing = pagesById[id]
      if (existing && Array.isArray(existing.blocks)) return
      pendingRef.current.add(id)
      getNotebook(id)
        .then((page) => setPagesById((prev) => ({ ...prev, [id]: page })))
        .catch(() => {})
        .finally(() => pendingRef.current.delete(id))
    })
  }, [entityIds, pagesById])

  const seed = useCallback((page) => {
    if (page?._id) setPagesById((prev) => ({ ...prev, [page._id]: page }))
  }, [])

  const remove = useCallback((id) => {
    setPagesById((prev) => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  return { pagesById, seed, remove }
}
